import Project from '@/models/project'
import { JobSearchResponse } from '@/models/types/job-search-response'
import { QueryObserverResult, useQueries } from '@tanstack/vue-query'
import { computed, ComputedRef, ref, unref, watch } from 'vue'
import { getProject } from './projects'
import { ProjectsQueryConfig } from './useProjectsQueries'
import type { ProjectJson } from '@ironsight/types'
import { AxiosResponse } from 'axios'
import type { MaybeRef } from '@vueuse/core'

export function useEnrichJobsWithProjects(
  jobs: ComputedRef<JobSearchResponse[]>,
  options: { enabled?: MaybeRef<boolean> } = {},
) {
  // NOTE:
  //  This ref array is needed due to Vue 2 being unable to detect reactivity of an array (`reactive([])`)
  // so there is a watch that will spread the result of `useQueries` into this ref in order to have something
  // that can detect reactivity.
  //  This will throw a warning regarding using Array as root value for reactive().
  // See: https://tanstack.com/query/v5/docs/framework/vue/guides/migrating-to-v5#usequeries-composable-returns-ref-instead-of-reactive
  const querySnapshot = ref<QueryObserverResult<Project, unknown>[]>([])

  const projectIds = computed(() => {
    if (!(unref(options.enabled) ?? true)) return []

    return [
      ...new Set(
        unref(jobs)
          .map((job) => job.project_id)
          .filter((id): id is string => !!id),
      ),
    ]
  })

  const queries = computed(() =>
    projectIds.value.map((projectId) => ({
      queryKey: [ProjectsQueryConfig.queryKey, 'details', projectId],
      queryFn: async () => {
        const response: AxiosResponse<ProjectJson> = await getProject(projectId)
        return new Project(response.data)
      },
      retry: false,
      staleTime: ProjectsQueryConfig.details.staleTime,
      cacheTime: ProjectsQueryConfig.details.gcTime,
    })),
  )

  const projectQueries = useQueries({ queries }) as unknown as ReadonlyArray<
    QueryObserverResult<Project, unknown>
  >

  // Manually update the snapshot when queries change
  watch(
    projectQueries,
    (newQueries) => {
      querySnapshot.value = [...newQueries]
    },
    { deep: true, immediate: true },
  )

  const projectMap = computed<Record<string, Project>>(() => {
    const map: Record<string, Project> = {}

    // Match by response identity, not observer position: the job page and
    // query results can update on different ticks during navigation.
    querySnapshot.value.forEach((query) => {
      if (query.isSuccess && query.data) {
        map[query.data.id] = query.data
      }
    })
    return map
  })

  const enrichedJobs = computed(() => {
    return unref(jobs).map((job) => {
      const project = job.project_id ? (projectMap.value[job.project_id] ?? null) : null
      return {
        ...job,
        project,
      }
    })
  })

  return {
    enrichedJobs,
    projectMap,
    projectQueries,
  }
}
